<template>
  <div class="combo-wrap">
    <button
      type="button"
      class="combo-trigger"
      :class="{ open: isOpen }"
      aria-haspopup="listbox"
      :aria-expanded="String(isOpen)"
      data-testid="preview-audience-trigger"
      @click="toggle"
    >
      <span class="trigger-text">{{ triggerLabel }}</span>
      <svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M6 9l6 6 6-6"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div class="panel" :class="{ open: isOpen }" role="listbox" aria-multiselectable="true">
      <div class="panel-search">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
          <path d="M21 21l-4.3-4.3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
        <input
          v-model="search"
          type="text"
          placeholder="Search groups…"
          autocomplete="off"
          data-testid="preview-audience-search"
        />
      </div>

      <div class="panel-list">
        <div
          class="opt opt-everyone"
          :class="{ selected: selectedCount === 0 }"
          role="option"
          data-testid="opt-all-content"
          @click="selectAllContent"
        >
          <v-simple-checkbox
            :value="selectedCount === 0"
            color="#077daf"
            :ripple="false"
            class="opt-checkbox"
            aria-label="All Content"
          />
          <span class="opt-label">All Content</span>
        </div>

        <div class="panel-divider"></div>

        <div
          class="opt"
          :class="{ selected: hasNoGroup }"
          role="option"
          data-testid="opt-no-group"
          @click="toggleNoGroup"
        >
          <v-simple-checkbox
            :value="hasNoGroup"
            color="#077daf"
            :ripple="false"
            class="opt-checkbox"
            :aria-label="NO_GROUP_LABEL"
          />
          <span class="opt-label">{{ NO_GROUP_LABEL }}</span>
        </div>

        <template v-if="pinnedGroups.length > 0">
          <div class="panel-divider"></div>
          <div class="panel-section-label">Selected</div>
          <transition-group name="pin" tag="div" class="pinned-list">
            <div
              v-for="group in pinnedGroups"
              :key="group.groupId"
              class="opt"
              :class="{ selected: true }"
              role="option"
              @click="toggleGroup(group)"
            >
              <v-simple-checkbox
                :value="true"
                color="#077daf"
                :ripple="false"
                class="opt-checkbox"
                :aria-label="group.groupDisplayName"
              />
              <span class="opt-label">{{ group.groupDisplayName }}</span>
            </div>
          </transition-group>
        </template>

        <div v-if="loading" class="panel-empty">Loading groups…</div>

        <template v-else>
          <div class="panel-divider" v-if="pinnedGroups.length > 0"></div>
          <transition-group name="pin" tag="div" class="unpinned-list">
            <div
              v-for="group in remainingFilteredGroups"
              :key="group.groupId"
              class="opt"
              :class="{ selected: isSelected(group.groupId) }"
              role="option"
              @click="toggleGroup(group)"
            >
              <v-simple-checkbox
                :value="isSelected(group.groupId)"
                color="#077daf"
                :ripple="false"
                class="opt-checkbox"
                :aria-label="group.groupDisplayName"
              />
              <span class="opt-label">{{ group.groupDisplayName }}</span>
            </div>
          </transition-group>
          <div v-if="filteredGroups.length === 0" class="panel-empty">
            No groups match "{{ search }}"
          </div>
        </template>
      </div>

      <div class="panel-footer">
        <span class="count">{{ footerText }}</span>
        <button
          type="button"
          class="clear-link"
          :disabled="selectedCount === 0"
          data-testid="preview-audience-reset"
          @click="reset"
        >
          Reset to All Content
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import GroupService from "@/services/GroupService";
import { mapState } from "vuex";

const NO_GROUP_ID = -1;
const NO_GROUP_LABEL = "Everyone (no group)";

export default {
  name: "PreviewAudienceSelector",
  props: {
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      isOpen: false,
      search: "",
      groups: [],
      loading: false,
      selectedGroups: [],
      NO_GROUP_LABEL,
    };
  },
  computed: {
    ...mapState(["globalClientId"]),
    selectedCount() {
      return this.selectedGroups.length;
    },
    hasNoGroup() {
      return this.selectedGroups.some((g) => g.groupId === NO_GROUP_ID);
    },
    filteredGroups() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.groups;
      return this.groups.filter((g) => (g.groupDisplayName || "").toLowerCase().includes(q));
    },
    pinnedGroups() {
      return this.selectedGroups.filter((g) => g.groupId !== NO_GROUP_ID);
    },
    remainingFilteredGroups() {
      if (this.pinnedGroups.length === 0) return this.filteredGroups;
      const pinnedIds = new Set(this.pinnedGroups.map((g) => g.groupId));
      return this.filteredGroups.filter((g) => !pinnedIds.has(g.groupId));
    },
    triggerLabel() {
      if (this.selectedCount === 0) return "All Content";
      const names = this.selectedGroups.map((g) => g.groupDisplayName);
      return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1} more`;
    },
    footerText() {
      return this.selectedCount === 0
        ? "All Content — no filter"
        : `${this.selectedCount} selected`;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.selectedGroups = Array.isArray(val) ? val.map((g) => ({ ...g })) : [];
      },
    },
    globalClientId() {
      this.fetchGroups();
    },
  },
  mounted() {
    this.fetchGroups();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    async fetchGroups() {
      this.loading = true;
      try {
        const response = await GroupService.getGroupsV2({
          filter: `status != 'Deleted' && clientId = ${this.globalClientId}`,
          offset: 0,
          limit: 9999,
          sort: "groupDisplayName ASC",
          expand: "null",
          count: 1,
        });
        this.groups = (response?.result?.rows || []).map((g) => ({
          groupId: g.groupId,
          groupDisplayName: g.groupDisplayName || g.name || `Group ${g.groupId}`,
        }));
      } catch (e) {
        console.error("Failed to load groups for preview", e);
        this.groups = [];
      } finally {
        this.loading = false;
      }
    },
    toggle() {
      this.isOpen = !this.isOpen;
    },
    handleOutsideClick(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    },
    isSelected(groupId) {
      return this.selectedGroups.some((g) => g.groupId === groupId);
    },
    selectAllContent() {
      this.selectedGroups = [];
      this.emit();
    },
    toggleNoGroup() {
      if (this.hasNoGroup) {
        this.selectedGroups = this.selectedGroups.filter((g) => g.groupId !== NO_GROUP_ID);
      } else {
        this.selectedGroups.push({ groupId: NO_GROUP_ID, groupDisplayName: NO_GROUP_LABEL });
      }
      this.emit();
    },
    toggleGroup(group) {
      if (this.isSelected(group.groupId)) {
        this.selectedGroups = this.selectedGroups.filter((g) => g.groupId !== group.groupId);
      } else {
        this.selectedGroups.push({
          groupId: group.groupId,
          groupDisplayName: group.groupDisplayName,
        });
      }
      this.emit();
    },
    reset() {
      this.selectedGroups = [];
      this.search = "";
      this.emit();
    },
    emit() {
      this.$emit("input", this.selectedGroups.slice());
    },
  },
};
</script>

<style scoped>
.combo-wrap {
  position: relative;
  min-width: 240px;
}
.combo-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 42, 78, 0.35);
  border-radius: 4px;
  padding: 6px 10px;
  color: #002a4e;
  cursor: pointer;
  width: 100%;
  min-width: 240px;
  justify-content: space-between;
  font-family: inherit;
  font-size: 14px;
}
.combo-trigger:hover {
  background: rgba(255, 255, 255, 0.75);
}
.combo-trigger .trigger-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 550;
}
.combo-trigger .chev {
  transition: transform 0.15s ease;
  flex-shrink: 0;
}
.combo-trigger.open .chev {
  transform: rotate(180deg);
}
.panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 320px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25), 0px 4px 8px 3px rgba(0, 0, 0, 0.15);
  color: rgba(0, 0, 0, 0.87);
  display: none;
  overflow: hidden;
  z-index: 30;
}
.panel.open {
  display: block;
}
.panel-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.panel-search svg {
  color: rgba(0, 0, 0, 0.6);
  flex-shrink: 0;
}
.panel-search input {
  border: none;
  outline: none;
  font-family: inherit;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  width: 100%;
  background: transparent;
}
.panel-search input::placeholder {
  color: rgba(0, 0, 0, 0.6);
}
.panel-list {
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 0;
}
.opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}
.opt:hover {
  background: #e4f2f5;
}
.opt.selected {
  background: rgba(9, 172, 236, 0.08);
}
.opt .opt-checkbox {
  flex-shrink: 0;
  pointer-events: none;
}
.opt .opt-checkbox ::v-deep .v-icon {
  font-size: 18px;
}
.opt .opt-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.opt-everyone {
  font-weight: 650;
}
.panel-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.12);
  margin: 2px 0;
}
.panel-section-label {
  padding: 6px 14px 2px;
  font-size: 11px;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.6);
}
.pinned-list {
  max-height: 140px;
  overflow-y: auto;
}
.pin-move {
  transition: transform 0.2s ease;
}
.panel-empty {
  padding: 18px 14px;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
  font-size: 14px;
}
.panel-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.panel-footer .count {
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
}
.panel-footer .clear-link {
  color: #077daf;
  font-weight: 550;
  cursor: pointer;
  background: none;
  border: none;
  font-family: inherit;
  font-size: 13px;
  padding: 0;
}
.panel-footer .clear-link:hover {
  text-decoration: underline;
}
.panel-footer .clear-link:disabled {
  color: rgba(0, 0, 0, 0.6);
  cursor: default;
  text-decoration: none;
}
</style>
